import api from './api'
import type { JournalEntryLine } from './accounting'

export interface HomebasePayrollRun {
  id: string
  name: string
  payments_count?: number | null
  approved_at?: string | null
  payday?: string | null
  total_cost?: string | null
  status?: string | null
  posted_je_id?: number | null
}

export interface HomebasePayrollRunsResponse {
  runs: HomebasePayrollRun[]
  count: number
}

export interface HomebaseSummaryRow {
  label: string
  amount: number | string
}

export interface HomebasePullResponse {
  run_id: string
  payday?: string | null
  txn_date?: string | null
  summary_rows: HomebaseSummaryRow[]
  report_rows: Record<string, unknown>[]
  summary_json: Record<string, unknown>
}

export interface HomebaseDraftRequest {
  run_id: string
  payroll_name?: string
}

export interface HomebaseDraftResponse {
  id: number
  lines: JournalEntryLine[]
  warnings: string[]
  private_note: string
  doc_number: string
  txn_date: string
  source_data: Record<string, unknown>
  total_debits: number | string
  total_credits: number | string
  status: string
}

export async function getHomebasePayrollRuns(
  startDate: string,
  endDate: string
): Promise<HomebasePayrollRunsResponse> {
  const response = await api.get<HomebasePayrollRunsResponse>('/homebase-payroll/runs', {
    params: { start_date: startDate, end_date: endDate },
  })
  return response.data
}

export async function pullHomebasePayrollRun(runId: string): Promise<HomebasePullResponse> {
  const response = await api.post<HomebasePullResponse>(
    `/homebase-payroll/runs/${encodeURIComponent(runId)}/pull`
  )
  return response.data
}

export async function createHomebaseDraft(
  data: HomebaseDraftRequest
): Promise<HomebaseDraftResponse> {
  const response = await api.post<HomebaseDraftResponse>('/homebase-payroll/draft', data)
  return response.data
}

export interface HomebasePullAllResponse {
  listed: number
  pulled: number
  skipped: number
  errors: Array<{ run_id: string; message: string }>
}

export async function pullAllHomebasePayrollRuns(
  start: string
): Promise<HomebasePullAllResponse> {
  const response = await api.post<HomebasePullAllResponse>('/homebase-payroll/runs/pull-all', null, {
    params: { start },
  })
  return response.data
}

export interface HomebaseRepostPreview {
  run_id: string
  je_id: string
  draft_id: number
  txn_date: string
  bank_amount: number | string
  old_wages_by_store: Record<string, number | string>
  new_wages_by_store: Record<string, number | string>
  old_benefits_by_store: Record<string, number | string>
  new_benefits_by_store: Record<string, number | string>
}

export async function previewHomebaseRepost(runId: string): Promise<HomebaseRepostPreview> {
  const response = await api.post<HomebaseRepostPreview>(
    `/homebase-payroll/runs/${encodeURIComponent(runId)}/repost-new-basis`,
    null,
    { params: { confirm: false } }
  )
  return response.data
}

export async function confirmHomebaseRepost(runId: string): Promise<HomebaseRepostPreview> {
  const response = await api.post<{ preview: HomebaseRepostPreview }>(
    `/homebase-payroll/runs/${encodeURIComponent(runId)}/repost-new-basis`,
    null,
    { params: { confirm: true } }
  )
  return response.data.preview
}
